import { GetServerSideProps } from 'next';
import { useEffect } from 'react';

import { assign, last } from 'radash';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { Component, getComponentPayloadValidator, QrScanType } from 'models';
import { getParam } from 'helpers/get-param';

import { ComponentDetail } from 'components/component-detail/ComponentDetail';
import { ComponentDetail as TeamComponentDetail } from 'components/component-detail/ComponentDetail.Team';
import { DatasheetMode } from 'components/datasheet/DatasheetMode';
import { Metadata } from 'components/page/Metadata';
import { QrScanGate } from 'components/showtime/components/QrScanGate';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useCurrentUser } from 'hooks/use-current-user';
import { ComponentService } from 'services/ComponentService';
import { UserService } from 'services/UserService';

const ComponentDetailPage = ({ component: serverComponent }: { component: Component }) => {
    const { uniqueName } = useComponentMeta(serverComponent);
    const user = useCurrentUser();

    const component = user ? serverComponent : getUnauthUserComponent(serverComponent);
    const isTeamComponent = ComponentService.isTeamComponent(serverComponent);

    useEffect(() => {
        if (!isTeamComponent) {
            UserService.pingProduct(component.id);
        }
    }, [component]);

    return (
        <>
            <Metadata title={uniqueName} description={component.description} />
            {isTeamComponent ? (
                <TeamComponentDetail component={component} />
            ) : (
                <ComponentDetail component={component} initialMode={DatasheetMode.VIEW} />
            )}
            <QrScanGate
                company={CollectionHelpers.id(component.manufacturer)}
                type={QrScanType.PRODUCT}
                itemId={component.id}
                title={uniqueName}
            />
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
    const componentIdWithSlug = getParam(params, 'componentId')!;
    const componentId = last(componentIdWithSlug.split('-'));

    // A slug ending in a dash leaves an empty id, which requests the components
    // list endpoint instead of a document and returns a truthy paginated envelope.
    if (!componentId) {
        return { notFound: true };
    }

    const component = await ComponentService.get(componentId, { depth: 0, req });

    if (!component) {
        return { notFound: true };
    }

    return {
        props: {
            component,
            metadata: {
                title: component.name,
            },
        },
    };
};

// Fills in defaults for logged-out visitors. A product that fails the strict validator (e.g. no electrical ports yet)
// is still shown as it is rather than crashing the page.
const getUnauthUserComponent = (component: Component) => {
    const parsed = getComponentPayloadValidator(component.type).safeParse(component);

    return parsed.success ? assign(parsed.data as Component, component) : component;
};

export default ComponentDetailPage;
